// CompanyKeyMetricsTTM.js
import React from 'react';
import {
    Container,
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatLargeNumber } from './StockChart/utils/formatters';

const CompanyKeyMetricsTTM = ({ data, financialRatiosTTM }) => {
    const theme = useTheme();

    const formatValue = (value, type) => {
        if (value === null || value === undefined) return '-';
        switch (type) {
            case 'currency':
                return formatLargeNumber(value);
            case 'ratio':
                return value.toFixed(2);
            case 'percentage':
                return `${(value * 100).toFixed(2)}%`;
            case 'days':
                return `${value.toFixed(1)} days`;
            default:
                return value.toLocaleString();
        }
    };

    const getValueColor = (value, type) => {
        if (value === null || value === undefined) return theme.palette.text.primary;
        if (type === 'percentage' || type === 'ratio') {
            if (value > 0) return theme.palette.positive.main;
            if (value < 0) return theme.palette.negative.main;
        }
        return theme.palette.text.primary;
    };

    const sections = [
        {
            title: 'Valuation',
            metrics: [
                { key: 'enterpriseValueTTM', label: 'Enterprise Value', type: 'currency' },
                { key: 'evToSalesTTM', label: 'EV / Sales', type: 'ratio' },
                { key: 'evToEBITDATTM', label: 'EV / EBITDA', type: 'ratio' },
                { key: 'evToOperatingCashFlowTTM', label: 'EV / Operating Cash Flow', type: 'ratio' },
                { key: 'evToFreeCashFlowTTM', label: 'EV / Free Cash Flow', type: 'ratio' },
                { key: 'earningsYieldTTM', label: 'Earnings Yield', type: 'percentage' },
                { key: 'freeCashFlowYieldTTM', label: 'Free Cash Flow Yield', type: 'percentage' },
                { key: 'grahamNumberTTM', label: 'Graham Number', type: 'ratio' },
                { key: 'grahamNetNetTTM', label: 'Graham Net-Net', type: 'ratio' },
            ]
        },
        {
            title: 'Returns',
            metrics: [
                { key: 'returnOnEquityTTM', label: 'Return on Equity (ROE)', type: 'percentage' },
                { key: 'returnOnAssetsTTM', label: 'Return on Assets (ROA)', type: 'percentage' },
                { key: 'returnOnInvestedCapitalTTM', label: 'Return on Invested Capital (ROIC)', type: 'percentage' },
                { key: 'returnOnCapitalEmployedTTM', label: 'Return on Capital Employed (ROCE)', type: 'percentage' },
                { key: 'operatingReturnOnAssetsTTM', label: 'Operating Return on Assets', type: 'percentage' },
                { key: 'returnOnTangibleAssetsTTM', label: 'Return on Tangible Assets', type: 'percentage' },
            ]
        },
        {
            title: 'Liquidity & Capital',
            metrics: [
                { key: 'currentRatioTTM', label: 'Current Ratio', type: 'ratio' },
                { key: 'workingCapitalTTM', label: 'Working Capital', type: 'currency' },
                { key: 'investedCapitalTTM', label: 'Invested Capital', type: 'currency' },
                { key: 'tangibleAssetValueTTM', label: 'Tangible Asset Value', type: 'currency' },
                { key: 'netCurrentAssetValueTTM', label: 'Net Current Asset Value', type: 'currency' },
                { key: 'netDebtToEBITDATTM', label: 'Net Debt / EBITDA', type: 'ratio' },
            ]
        },
        {
            title: 'Quality & Burden',
            metrics: [
                { key: 'incomeQualityTTM', label: 'Income Quality', type: 'ratio' },
                { key: 'taxBurdenTTM', label: 'Tax Burden', type: 'ratio' },
                { key: 'interestBurdenTTM', label: 'Interest Burden', type: 'ratio' },
            ]
        },
        {
            title: 'Cash Flow Efficiency',
            metrics: [
                { key: 'freeCashFlowToEquityTTM', label: 'FCF to Equity', type: 'currency' },
                { key: 'freeCashFlowToFirmTTM', label: 'FCF to Firm', type: 'currency' },
                { key: 'capexToOperatingCashFlowTTM', label: 'CapEx / Operating Cash Flow', type: 'percentage' },
                { key: 'capexToRevenueTTM', label: 'CapEx / Revenue', type: 'percentage' },
                { key: 'capexToDepreciationTTM', label: 'CapEx / Depreciation', type: 'ratio' },
            ]
        },
        {
            title: 'Expense Ratios',
            metrics: [
                { key: 'researchAndDevelopementToRevenueTTM', label: 'R&D / Revenue', type: 'percentage' },
                { key: 'salesGeneralAndAdministrativeToRevenueTTM', label: 'SG&A / Revenue', type: 'percentage' },
                { key: 'stockBasedCompensationToRevenueTTM', label: 'SBC / Revenue', type: 'percentage' },
                { key: 'intangiblesToTotalAssetsTTM', label: 'Intangibles / Total Assets', type: 'percentage' },
            ]
        },
        {
            title: 'Working Capital Cycle',
            metrics: [
                { key: 'daysOfSalesOutstandingTTM', label: 'Days Sales Outstanding', type: 'days' },
                { key: 'daysOfInventoryOutstandingTTM', label: 'Days Inventory Outstanding', type: 'days' },
                { key: 'daysOfPayablesOutstandingTTM', label: 'Days Payables Outstanding', type: 'days' },
                { key: 'operatingCycleTTM', label: 'Operating Cycle', type: 'days' },
                { key: 'cashConversionCycleTTM', label: 'Cash Conversion Cycle', type: 'days' },
                { key: 'averageReceivablesTTM', label: 'Average Receivables', type: 'currency' },
                { key: 'averagePayablesTTM', label: 'Average Payables', type: 'currency' },
                { key: 'averageInventoryTTM', label: 'Average Inventory', type: 'currency' },
            ]
        },
        ...(financialRatiosTTM ? [
            {
                title: 'Margins',
                source: 'ratios',
                metrics: [
                    { key: 'grossProfitMarginTTM', label: 'Gross Profit Margin', type: 'percentage' },
                    { key: 'operatingProfitMarginTTM', label: 'Operating Profit Margin', type: 'percentage' },
                    { key: 'ebitdaMarginTTM', label: 'EBITDA Margin', type: 'percentage' },
                    { key: 'ebitMarginTTM', label: 'EBIT Margin', type: 'percentage' },
                    { key: 'pretaxProfitMarginTTM', label: 'Pretax Profit Margin', type: 'percentage' },
                    { key: 'netProfitMarginTTM', label: 'Net Profit Margin', type: 'percentage' },
                    { key: 'bottomLineProfitMarginTTM', label: 'Bottom Line Profit Margin', type: 'percentage' },
                ]
            },
            {
                title: 'Price Ratios',
                source: 'ratios',
                metrics: [
                    { key: 'priceToEarningsRatioTTM', label: 'P/E Ratio', type: 'ratio' },
                    { key: 'priceToEarningsGrowthRatioTTM', label: 'PEG Ratio', type: 'ratio' },
                    { key: 'forwardPriceToEarningsGrowthRatioTTM', label: 'Forward PEG Ratio', type: 'ratio' },
                    { key: 'priceToBookRatioTTM', label: 'P/B Ratio', type: 'ratio' },
                    { key: 'priceToSalesRatioTTM', label: 'P/S Ratio', type: 'ratio' },
                    { key: 'priceToFreeCashFlowRatioTTM', label: 'P/FCF Ratio', type: 'ratio' },
                    { key: 'priceToOperatingCashFlowRatioTTM', label: 'P/OCF Ratio', type: 'ratio' },
                    { key: 'priceToFairValueTTM', label: 'Price to Fair Value', type: 'ratio' },
                ]
            },
            {
                title: 'Leverage',
                source: 'ratios',
                metrics: [
                    { key: 'debtToEquityRatioTTM', label: 'Debt / Equity', type: 'ratio' },
                    { key: 'debtToAssetsRatioTTM', label: 'Debt / Assets', type: 'ratio' },
                    { key: 'debtToCapitalRatioTTM', label: 'Debt / Capital', type: 'ratio' },
                    { key: 'longTermDebtToCapitalRatioTTM', label: 'LT Debt / Capital', type: 'ratio' },
                    { key: 'financialLeverageRatioTTM', label: 'Financial Leverage', type: 'ratio' },
                    { key: 'debtToMarketCapTTM', label: 'Debt / Market Cap', type: 'ratio' },
                ]
            },
            {
                title: 'Liquidity',
                source: 'ratios',
                metrics: [
                    { key: 'quickRatioTTM', label: 'Quick Ratio', type: 'ratio' },
                    { key: 'cashRatioTTM', label: 'Cash Ratio', type: 'ratio' },
                    { key: 'solvencyRatioTTM', label: 'Solvency Ratio', type: 'ratio' },
                ]
            },
            {
                title: 'Turnover',
                source: 'ratios',
                metrics: [
                    { key: 'assetTurnoverTTM', label: 'Asset Turnover', type: 'ratio' },
                    { key: 'fixedAssetTurnoverTTM', label: 'Fixed Asset Turnover', type: 'ratio' },
                    { key: 'inventoryTurnoverTTM', label: 'Inventory Turnover', type: 'ratio' },
                    { key: 'receivablesTurnoverTTM', label: 'Receivables Turnover', type: 'ratio' },
                    { key: 'payablesTurnoverTTM', label: 'Payables Turnover', type: 'ratio' },
                    { key: 'workingCapitalTurnoverRatioTTM', label: 'Working Capital Turnover', type: 'ratio' },
                ]
            },
            {
                title: 'Coverage',
                source: 'ratios',
                metrics: [
                    { key: 'interestCoverageRatioTTM', label: 'Interest Coverage', type: 'ratio' },
                    { key: 'debtServiceCoverageRatioTTM', label: 'Debt Service Coverage', type: 'ratio' },
                    { key: 'capitalExpenditureCoverageRatioTTM', label: 'CapEx Coverage', type: 'ratio' },
                    { key: 'dividendPaidAndCapexCoverageRatioTTM', label: 'Dividend + CapEx Coverage', type: 'ratio' },
                    { key: 'operatingCashFlowCoverageRatioTTM', label: 'Operating CF Coverage', type: 'ratio' },
                    { key: 'operatingCashFlowSalesRatioTTM', label: 'Operating CF / Sales', type: 'ratio' },
                    { key: 'freeCashFlowOperatingCashFlowRatioTTM', label: 'FCF / Operating CF', type: 'ratio' },
                ]
            },
            {
                title: 'Dividends',
                source: 'ratios',
                metrics: [
                    { key: 'dividendYieldTTM', label: 'Dividend Yield', type: 'percentage' },
                    { key: 'dividendPayoutRatioTTM', label: 'Payout Ratio', type: 'percentage' },
                    { key: 'dividendPerShareTTM', label: 'Dividend Per Share', type: 'ratio' },
                ]
            },
            {
                title: 'Per Share',
                source: 'ratios',
                metrics: [
                    { key: 'revenuePerShareTTM', label: 'Revenue / Share', type: 'ratio' },
                    { key: 'netIncomePerShareTTM', label: 'Net Income / Share', type: 'ratio' },
                    { key: 'bookValuePerShareTTM', label: 'Book Value / Share', type: 'ratio' },
                    { key: 'tangibleBookValuePerShareTTM', label: 'Tangible Book Value / Share', type: 'ratio' },
                    { key: 'cashPerShareTTM', label: 'Cash / Share', type: 'ratio' },
                    { key: 'operatingCashFlowPerShareTTM', label: 'Operating CF / Share', type: 'ratio' },
                    { key: 'freeCashFlowPerShareTTM', label: 'FCF / Share', type: 'ratio' },
                    { key: 'interestDebtPerShareTTM', label: 'Interest Debt / Share', type: 'ratio' },
                ]
            },
            {
                title: 'Tax & Earnings Quality',
                source: 'ratios',
                metrics: [
                    { key: 'effectiveTaxRateTTM', label: 'Effective Tax Rate', type: 'percentage' },
                    { key: 'netIncomePerEBTTTM', label: 'Net Income / EBT', type: 'ratio' },
                    { key: 'ebtPerEbitTTM', label: 'EBT / EBIT', type: 'ratio' },
                ]
            },
        ] : [])
    ];

    if (!data) {
        return (
            <Container>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <Typography variant="h6" color="textSecondary">
                        No key metrics TTM data available
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2, md: 3 } }}>
            <Box my={4}>
                <Box sx={{
                    columnCount: { xs: 1, md: 2 },
                    columnGap: 3,
                    '& > *': {
                        breakInside: 'avoid',
                        mb: 3
                    }
                }}>
                    {sections.map((section) => (
                        <Box key={section.title}>
                            <TableContainer
                                component={Paper}
                                variant="stats"
                                elevation={3}
                            >
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                colSpan={2}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    fontSize: { xs: '0.85rem', sm: '1rem' },
                                                    color: theme.palette.primary.main,
                                                    py: 1.5
                                                }}
                                            >
                                                {section.title}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {section.metrics.map((metric) => (
                                            <TableRow
                                                key={metric.key}
                                                hover
                                                sx={{
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.mode === 'dark'
                                                            ? 'rgba(255, 255, 255, 0.05)'
                                                            : 'rgba(0, 0, 0, 0.02)'
                                                    }
                                                }}
                                            >
                                                <TableCell
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                        py: { xs: 0.75, sm: 1 }
                                                    }}
                                                >
                                                    {metric.label}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                        py: { xs: 0.75, sm: 1 },
                                                        color: getValueColor((section.source === 'ratios' ? financialRatiosTTM : data)?.[metric.key], metric.type)
                                                    }}
                                                >
                                                    {formatValue((section.source === 'ratios' ? financialRatiosTTM : data)?.[metric.key], metric.type)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Container>
    );
};

export default CompanyKeyMetricsTTM;